/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions */
import React, { useEffect, useMemo, useRef, useState } from 'react'

import DOMPurify from 'dompurify'
import PropTypes from 'prop-types'

export function getAcceptActionLabel(entity) {
  if (!entity) return 'Accept'
  const hasCurrent = Boolean(entity.currentValue && String(entity.currentValue).trim().length > 0)
  const category = (entity.category || '').toLowerCase()
  const field = entity.field || ''

  if (entity.isNegativeFinding) {
    return 'Discard Negative Finding'
  }

  // Provider
  if (category === 'provider') {
    if (entity.isConsolidated || (entity.totalOccurrences > 1 && entity.occurrenceIndex === 1)) {
      return 'Consolidate Provider'
    }
    const matchType = entity.providerMatch?.type || (entity.duplicateMatch ? 'case' : null)
    if (matchType === 'case') {
      return hasCurrent ? 'Update Case Provider' : 'Confirm Case Provider'
    }
    if (matchType === 'library') {
      return 'Link Library Provider to Case'
    }
    if (entity.attributes?.practiceName || entity.organizationName) {
      return 'Accept Linked Provider'
    }
    return hasCurrent ? 'Update Provider' : 'Add Treatment Provider'
  }

  // Diagnosis
  if (category === 'diagnosis') {
    if (entity.isConsolidated || (entity.totalOccurrences > 1 && entity.occurrenceIndex === 1)) {
      return 'Consolidate Diagnoses'
    }
    if (entity.isPreExisting) {
      return 'Add Pre-Existing Condition'
    }
    return hasCurrent ? 'Update Diagnosis' : 'Add Diagnosis'
  }

  if (entity.isConsolidated) {
    return 'Consolidate'
  }

  // Specific Demographics & Referral fields
  if (field === 'Date of Injury') {
    return hasCurrent ? 'Change Date of Injury' : 'Set Date of Injury'
  }
  if (field === 'Injury Description') {
    return hasCurrent ? 'Update Injury Description' : 'Add Injury Narrative'
  }
  if (field === 'Mechanism of Injury') {
    return hasCurrent ? 'Update Mechanism of Injury' : 'Set Mechanism of Injury'
  }
  if (field === 'Nature of Injury') {
    return hasCurrent ? 'Update Nature of Injury' : 'Set Nature of Injury'
  }
  if (field === 'Employer') {
    return hasCurrent ? 'Update Employer' : 'Set Employer'
  }
  if (field === 'Date of Hire') {
    return hasCurrent ? 'Update Date of Hire' : 'Set Date of Hire'
  }
  if (field === 'Claimant Name' || field === 'Client Name') {
    return hasCurrent ? 'Update Claimant Name' : 'Set Claimant Name'
  }
  if (field === 'Date of Birth') {
    return hasCurrent ? 'Change Date of Birth' : 'Set Date of Birth'
  }
  if (field === 'Claimant Phone' || field === 'Phone Number' || field === 'Phone') {
    return hasCurrent ? 'Update Phone Number' : 'Set Phone Number'
  }
  if (field === 'Claimant Address' || field === 'Address') {
    return hasCurrent ? 'Update Address' : 'Set Address'
  }
  if (field === 'Occupation' || field === 'Claimant Occupation') {
    return hasCurrent ? 'Update Occupation' : 'Set Occupation'
  }
  if (field === 'Claim Number') {
    return hasCurrent ? 'Update Claim Number' : 'Set Claim Number'
  }
  if (field === 'Policy Number') {
    return hasCurrent ? 'Update Policy Number' : 'Set Policy Number'
  }
  if (field === 'Insurance Carrier' || field === 'Insurer') {
    return hasCurrent ? 'Update Insurance Carrier' : 'Set Insurance Carrier'
  }

  // Tasks, Activities & Contacts
  if (category === 'task') {
    return 'Accept Task'
  }
  if (category === 'contact' || category === 'keycontact') {
    return 'Add to Key Contacts'
  }
  if (category === 'activity') {
    return 'Add Activity'
  }

  // Other clinical categories
  if (category === 'appointment') {
    return 'Add Appointment'
  }
  if (category === 'test') {
    return 'Add Diagnostic Test'
  }
  if (category === 'medication') {
    return 'Add Medication'
  }
  if (category === 'allergy') {
    return 'Add Allergy'
  }
  if (category === 'surgery') {
    return 'Add Surgical Procedure'
  }
  if (category === 'status') {
    return hasCurrent ? 'Update Work Status' : 'Set Work Status'
  }

  // Fallback
  return hasCurrent ? `Update ${field}` : `Add ${field}`
}

export function getRejectionOptions(category) {
  switch (category) {
    case 'diagnosis':
      return ['Not relevant to this case', 'Pre-existing / Not injury-related', 'Duplicate diagnosis', 'Other']
    case 'provider':
      return [
        'Procedure predates injury',
        'Not a treating provider',
        'Second opinion / IME',
        'Duplicate provider',
        'Other',
      ]
    case 'test':
      return ['Predates injury', 'Duplicate test result', 'Not applicable to case', 'Other']
    case 'surgery':
      return ['Pre-existing surgery', 'Not injury-related', 'Duplicate', 'Other']
    case 'medication':
    case 'allergy':
      return ['Not current / discontinued', 'Duplicate medication', 'Other']
    case 'appointment':
      return ['Past / already occurred', 'Duplicate appointment', 'Cancelled', 'Other']
    case 'status':
      return ['Superseded by newer note', 'Pre-existing work status', 'Other']
    case 'task':
      return [
        'Already completed',
        'Not required for this case',
        'Handled by employer / adjuster',
        'Duplicate task',
        'Other',
      ]
    case 'contact':
    case 'keycontact':
      return ['Not a relevant contact', 'Duplicate contact', 'No longer with facility', 'Other']
    case 'activity':
      return ['Non-billable / Administrative', 'Duplicate activity', 'Not performed', 'Other']
    case 'referral':
    case 'demographics':
      return ['Keep existing case value', 'Incorrect extraction', 'Other']
    default:
      return ['Not applicable to this case', 'Duplicate', 'Other']
  }
}

export default function EntityCard({
  entity,
  isActive,
  onSelect,
  onAccept,
  onReject,
  onMerge,
  onAssociateOrganization,
  onEdit,
  onUndo,
  onUpdateMedicationFlags,
  availableOrganizations = [],
  readOnly = false,
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(entity.extractedValue || '')
  const [showRejectMenu, setShowRejectMenu] = useState(false)
  const [customReason, setCustomReason] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [showOccurrences, setShowOccurrences] = useState(false)
  const [isCustomPractice, setIsCustomPractice] = useState(false)
  const [customPracticeName, setCustomPracticeName] = useState('')
  const acceptActionLabel = useMemo(() => getAcceptActionLabel(entity), [entity])

  const [localMedFlags, setLocalMedFlags] = useState({
    relatedToInjury: Boolean(entity.attributes?.relatedToInjury),
    preInjury: Boolean(entity.attributes?.preInjury || entity.attributes?.isPreInjury),
    discontinued: Boolean(entity.attributes?.discontinued || entity.attributes?.isDiscontinued),
  })

  useEffect(() => {
    setLocalMedFlags({
      relatedToInjury: Boolean(entity.attributes?.relatedToInjury),
      preInjury: Boolean(entity.attributes?.preInjury || entity.attributes?.isPreInjury),
      discontinued: Boolean(entity.attributes?.discontinued || entity.attributes?.isDiscontinued),
    })
  }, [
    entity.attributes?.relatedToInjury,
    entity.attributes?.preInjury,
    entity.attributes?.isPreInjury,
    entity.attributes?.discontinued,
    entity.attributes?.isDiscontinued,
  ])

  const handleMedFlagChange = (key, checked) => {
    const updated = {
      ...localMedFlags,
      [key]: checked,
      ...(key === 'preInjury' ? { isPreInjury: checked } : {}),
      ...(key === 'discontinued' ? { isDiscontinued: checked } : {}),
    }
    setLocalMedFlags(updated)
    if (entity.attributes) {
      entity.attributes[key] = checked
      if (key === 'preInjury') entity.attributes.isPreInjury = checked
      if (key === 'discontinued') entity.attributes.isDiscontinued = checked
    }
    if (onUpdateMedicationFlags) {
      onUpdateMedicationFlags(entity.id, {
        [key]: checked,
        ...(key === 'preInjury' ? { isPreInjury: checked } : {}),
        ...(key === 'discontinued' ? { isDiscontinued: checked } : {}),
      })
    }
  }

  const editInputRef = useRef(null)
  const customReasonInputRef = useRef(null)
  const customPracticeInputRef = useRef(null)
  const cardRef = useRef(null)

  useEffect(() => {
    setEditValue(entity.extractedValue || '')
  }, [entity.extractedValue])

  // Scroll active card into view strictly within the cards scroll area
  useEffect(() => {
    if (isActive && cardRef.current) {
      const scrollContainer = cardRef.current.closest('.hitl-cards-scroll-area')
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect()
        const cardRect = cardRef.current.getBoundingClientRect()
        const isVisible = cardRect.top >= containerRect.top + 8 && cardRect.bottom <= containerRect.bottom - 8
        if (!isVisible) {
          const relativeTop = cardRect.top - containerRect.top + scrollContainer.scrollTop
          const targetScrollTop = Math.max(0, relativeTop - 10)
          if (typeof scrollContainer.scrollTo === 'function') {
            scrollContainer.scrollTo({
              top: targetScrollTop,
              behavior: 'smooth',
            })
          } else {
            scrollContainer.scrollTop = targetScrollTop
          }
        }
      }
    }
  }, [isActive])

  // Programmatic focus on edit input (Project rule: NEVER use JSX autoFocus)
  useEffect(() => {
    if (isEditing && editInputRef.current) {
      editInputRef.current.focus()
    }
  }, [isEditing])

  useEffect(() => {
    if (showCustomInput && customReasonInputRef.current) {
      customReasonInputRef.current.focus()
    }
  }, [showCustomInput])

  useEffect(() => {
    if (isCustomPractice && customPracticeInputRef.current) {
      customPracticeInputRef.current.focus()
    }
  }, [isCustomPractice])

  const handleSaveEdit = e => {
    if (e) e.preventDefault()
    onEdit(entity.id, editValue)
    setIsEditing(false)
  }

  const handleAcceptClick = () => {
    if (isEditing) {
      onEdit(entity.id, editValue)
      setIsEditing(false)
    }
    onAccept(entity.id)
  }

  const handleCancelEdit = () => {
    setEditValue(entity.extractedValue || '')
    setIsEditing(false)
  }

  const handleSelectRejectReason = reason => {
    if (reason === 'Other') {
      setShowCustomInput(true)
    } else {
      setShowRejectMenu(false)
      setShowCustomInput(false)
      onReject(entity.id, reason)
    }
  }

  const handleCustomReasonSubmit = e => {
    if (e) e.preventDefault()
    if (customReason.trim()) {
      setShowRejectMenu(false)
      setShowCustomInput(false)
      onReject(entity.id, customReason.trim())
      setCustomReason('')
    }
  }

  const getCategoryBadgeClass = category => {
    switch (category) {
      case 'referral':
      case 'demographics':
        return 'badge-primary'
      case 'diagnosis':
        return 'badge-info'
      case 'provider':
        return 'badge-dark'
      case 'test':
        return 'badge-primary'
      case 'medication':
        return 'badge-secondary'
      case 'allergy':
        return 'badge-warning text-dark'
      case 'appointment':
        return 'badge-info'
      case 'surgery':
        return 'badge-danger'
      case 'status':
        return 'badge-warning text-dark'
      case 'task':
        return 'badge-warning text-dark'
      case 'contact':
      case 'keycontact':
        return 'badge-secondary'
      case 'activity':
        return 'badge-info'
      default:
        return 'badge-primary'
    }
  }

  const isClaimOrPolicyField = entity.field === 'Claim Number' || entity.field === 'Policy Number'
  const hasDiff = entity.currentValue && entity.currentValue !== entity.extractedValue && entity.status !== 'rejected'

  return (
    <div
      ref={cardRef}
      className={`hitl-entity-card ${isActive ? 'active' : ''} ${
        entity.status === 'accepted'
          ? 'status-accepted'
          : entity.status === 'rejected'
          ? 'status-rejected'
          : entity.status === 'merged'
          ? 'status-merged'
          : entity.status === 'edited'
          ? 'status-edited'
          : ''
      }`}
      onClick={() => onSelect && onSelect(entity)}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          onSelect && onSelect(entity)
        }
      }}
    >
      {/* Badges Row */}
      <div className="hitl-badge-group d-flex flex-wrap align-items-center" style={{ gap: '4px' }}>
        <span className={`badge ${getCategoryBadgeClass(entity.category)} text-uppercase`}>{entity.category}</span>

        {/* Active Focus Indicator Badge */}
        {isActive && (
          <span
            className="badge badge-primary font-weight-bold ml-auto"
            style={{ fontSize: '0.72rem', padding: '0.25em 0.55em' }}
          >
            <i className="fas fa-crosshairs mr-1" />
            Current Focus
          </span>
        )}

        {/* Consolidated Badge */}
        {entity.isConsolidated && (
          <span
            className="badge badge-primary font-weight-bold"
            title={`Consolidated from ${entity.clusterCount || entity.totalOccurrences} mentions across documents`}
            style={{ fontSize: '0.72rem' }}
          >
            <i className="fas fa-layer-group mr-1" />
            Consolidated Candidate ({entity.clusterCount || entity.totalOccurrences} mentions)
          </span>
        )}

        {/* Negative Finding Badge */}
        {entity.isNegativeFinding && (
          <span
            className="badge badge-warning text-dark font-weight-bold"
            title="Negative imaging/lab finding — not an active condition or diagnosis"
            style={{ fontSize: '0.72rem' }}
          >
            <i className="fas fa-exclamation-triangle mr-1" />
            Negative Finding
          </span>
        )}

        {/* Pre-Existing / Incidental Badge */}
        {entity.isPreExisting && (
          <span
            className="badge badge-info font-weight-bold"
            title="Pre-existing or degenerative condition"
            style={{ fontSize: '0.72rem' }}
          >
            <i className="fas fa-history mr-1" />
            Pre-Existing / Incidental
          </span>
        )}

        {/* Radiology Impression Badge */}
        {entity.isRadiologyImpression && (
          <span
            className="badge badge-secondary font-weight-bold"
            title="Long radiology narrative excerpt"
            style={{ fontSize: '0.72rem' }}
          >
            <i className="fas fa-x-ray mr-1" />
            Radiology Narrative
          </span>
        )}

        {/* Multi-Occurrence Pill */}
        {entity.totalOccurrences > 1 && !entity.isConsolidated && (
          <span
            className="badge badge-warning text-dark font-weight-bold"
            title={`Occurrence ${entity.occurrenceIndex} of ${entity.totalOccurrences} across documents`}
            style={{ fontSize: '0.72rem' }}
          >
            <i className="fas fa-history mr-1" />
            Occurrence {entity.occurrenceIndex} of {entity.totalOccurrences}
          </span>
        )}

        {/* Source Document Tag */}
        {entity.documentName && (
          <span
            className="badge badge-light border text-muted"
            title={entity.documentName}
            style={{ fontSize: '0.72rem' }}
          >
            <i className="fas fa-file-alt mr-1" />
            {entity.documentName.length > 20 ? `${entity.documentName.slice(0, 18)}…` : entity.documentName}
          </span>
        )}

        {entity.isNpiVerified && (
          <span className="hitl-npi-pill" title="Verified against NPI Registry">
            <i className="fas fa-check-circle mr-1" />
            NPI Verified
          </span>
        )}

        {entity.category === 'provider'
          ? (() => {
              const pMatch =
                entity.providerMatch ||
                (entity.duplicateMatch
                  ? { type: 'case', matchedName: entity.duplicateMatch.existingName }
                  : { type: 'new' })
              if (pMatch.type === 'case') {
                return (
                  <span
                    className="badge badge-success font-weight-bold"
                    title={`Matches provider already attached to this case: "${
                      pMatch.matchedName || entity.extractedValue
                    }"`}
                    style={{ fontSize: '0.72rem' }}
                  >
                    <i className="fas fa-check-circle mr-1" />
                    Already on Case: {pMatch.matchedName || 'Match'}
                  </span>
                )
              }
              if (pMatch.type === 'library') {
                return (
                  <span
                    className="badge badge-primary font-weight-bold text-white"
                    title={`Matches existing provider in Library: "${
                      pMatch.matchedName || entity.extractedValue
                    }". Accepting will link to this case.`}
                    style={{ fontSize: '0.72rem', backgroundColor: '#4f46e5', borderColor: '#4338ca' }}
                  >
                    <i className="fas fa-book-medical mr-1" />
                    In Provider Library: {pMatch.matchedName || 'Match'}
                  </span>
                )
              }
              return (
                <span
                  className="badge badge-secondary font-weight-bold"
                  title="Not currently in Provider Library or on this Case. Accepting will add to Library and link to this case."
                  style={{ fontSize: '0.72rem' }}
                >
                  <i className="fas fa-plus-circle mr-1" />
                  New to Library & Case
                </span>
              )
            })()
          : entity.duplicateMatch && (
              <span
                className="hitl-dedup-pill"
                title={`Matched with existing case record: "${entity.duplicateMatch.existingName}"`}
              >
                <i className="fas fa-link mr-1" />
                Matched: {entity.duplicateMatch.existingName}
                {entity.duplicateMatch.confidence ? ` (${Math.round(entity.duplicateMatch.confidence * 100)}%)` : ''}
              </span>
            )}

        {isClaimOrPolicyField && (
          <span className="hitl-isolation-badge">
            <i className="fas fa-shield-alt mr-1" />
            {entity.field === 'Claim Number' ? 'Carrier Claim #' : 'Insurance Policy #'}
          </span>
        )}

        {/* Status Indicators */}
        {entity.status === 'accepted' && (
          <div className="d-flex align-items-center ml-auto" style={{ gap: '6px' }}>
            <span className="badge badge-success">
              <i className="fas fa-check mr-1" /> Accepted
            </span>
            {!readOnly && (
              <button
                type="button"
                className="btn btn-link btn-xs text-muted p-0"
                title="Undo decision and reopen as Pending"
                style={{ fontSize: '0.75rem', textDecoration: 'none' }}
                onClick={e => {
                  e.stopPropagation()
                  if (onUndo) onUndo(entity.id)
                }}
              >
                <i className="fas fa-undo mr-1 text-secondary" /> Reopen
              </button>
            )}
          </div>
        )}
        {entity.status === 'merged' && (
          <div className="d-flex align-items-center ml-auto" style={{ gap: '6px' }}>
            <span
              className="badge badge-info"
              title={entity.mergedIntoTitle ? `Merged into: ${entity.mergedIntoTitle}` : 'Merged with accepted record'}
            >
              <i className="fas fa-bolt mr-1" /> Merged
            </span>
            {!readOnly && (
              <button
                type="button"
                className="btn btn-link btn-xs text-muted p-0"
                title="Undo merge and reopen as Pending"
                style={{ fontSize: '0.75rem', textDecoration: 'none' }}
                onClick={e => {
                  e.stopPropagation()
                  if (onUndo) onUndo(entity.id)
                }}
              >
                <i className="fas fa-undo mr-1 text-secondary" /> Reopen
              </button>
            )}
          </div>
        )}
        {entity.status === 'rejected' && (
          <div className="d-flex align-items-center ml-auto" style={{ gap: '6px' }}>
            <span className="badge badge-secondary" title={entity.rejectionReason || 'Skipped'}>
              <i className="fas fa-times mr-1" /> Skipped{entity.rejectionReason ? `: ${entity.rejectionReason}` : ''}
            </span>
            {!readOnly && (
              <button
                type="button"
                className="btn btn-link btn-xs text-muted p-0"
                title="Undo skip and reopen as Pending"
                style={{ fontSize: '0.75rem', textDecoration: 'none' }}
                onClick={e => {
                  e.stopPropagation()
                  if (onUndo) onUndo(entity.id)
                }}
              >
                <i className="fas fa-undo mr-1 text-secondary" /> Reopen
              </button>
            )}
          </div>
        )}
        {entity.status === 'edited' && (
          <span className="badge badge-info ml-auto">
            <i className="fas fa-pencil-alt mr-1" /> Edited
          </span>
        )}
      </div>

      {/* Field Label & Source Precision Badge */}
      <div className="hitl-field-title d-flex justify-content-between align-items-center mt-1">
        <span>{entity.field}</span>
        {(entity.page || entity.lineNumber) && (
          <span className="badge badge-light border text-muted font-weight-normal" style={{ fontSize: '0.72rem' }}>
            <i className="fas fa-map-marker-alt text-danger mr-1" />
            {entity.page && entity.lineNumber
              ? `Page ${entity.page} • Line ${entity.lineNumber}`
              : entity.lineNumber
              ? `Line ${entity.lineNumber}`
              : `Page ${entity.page}`}
          </span>
        )}
      </div>

      {/* Value or Inline Edit */}
      {isEditing ? (
        <form onSubmit={handleSaveEdit} className="my-2" onClick={e => e.stopPropagation()}>
          <div className="form-group mb-2">
            {entity.category === 'test' ||
            (editValue && editValue.includes('\n')) ||
            (editValue && editValue.length > 80) ? (
              <textarea
                ref={editInputRef}
                className="form-control form-control-sm"
                rows={Math.min(12, Math.max(5, (editValue.match(/\n/g) || []).length + 2))}
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                placeholder={`Enter ${entity.field} results`}
                style={{ fontSize: '0.85rem', lineHeight: '1.45', fontFamily: 'inherit' }}
              />
            ) : (
              <input
                ref={editInputRef}
                type="text"
                className="form-control form-control-sm"
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                placeholder={`Enter ${entity.field}`}
              />
            )}
            {entity.category === 'medication' && (
              <div
                className="d-flex flex-wrap align-items-center mt-2 mb-2 pt-1 border-top"
                style={{ gap: '0.85rem', fontSize: '0.78rem' }}
              >
                <label className="mb-0 d-flex align-items-center cursor-pointer font-weight-normal text-secondary">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={Boolean(localMedFlags.relatedToInjury)}
                    onChange={e => handleMedFlagChange('relatedToInjury', e.target.checked)}
                  />
                  Related to Injury
                </label>
                <label className="mb-0 d-flex align-items-center cursor-pointer font-weight-normal text-secondary">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={Boolean(localMedFlags.preInjury)}
                    onChange={e => handleMedFlagChange('preInjury', e.target.checked)}
                  />
                  Pre-injury
                </label>
                <label className="mb-0 d-flex align-items-center cursor-pointer font-weight-normal text-secondary">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={Boolean(localMedFlags.discontinued)}
                    onChange={e => handleMedFlagChange('discontinued', e.target.checked)}
                  />
                  Discontinued
                </label>
              </div>
            )}
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted font-italic">
              {entity.category === 'test' ? 'Edit or trim results before applying to case' : ''}
            </small>
            <div className="d-flex" style={{ gap: '6px' }}>
              <button
                type="button"
                className="btn btn-xs btn-outline-secondary"
                onClick={handleCancelEdit}
                style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-xs btn-primary"
                style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem' }}
              >
                Save
              </button>
            </div>
          </div>
        </form>
      ) : (
        <>
          {/* Diff Box if updating an existing case field */}
          {hasDiff ? (
            <div className="hitl-diff-box">
              <div className="diff-label">Proposed Update:</div>
              <div className="diff-removed">- {entity.currentValue}</div>
              <div className="diff-added">+ {entity.extractedValue}</div>
            </div>
          ) : (
            <div
              className={`hitl-value-display ${entity.category === 'test' ? '' : 'font-weight-bold'}`}
              style={
                entity.category === 'test' || (entity.extractedValue && entity.extractedValue.includes('\n'))
                  ? { whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: '1.45' }
                  : undefined
              }
            >
              {entity.extractedValue || <span className="text-muted font-italic">None detected</span>}
            </div>
          )}

          {entity.category === 'task' && entity.attributes?.description && (
            <div className="hitl-task-description text-muted small mt-1 mb-2" style={{ lineHeight: '1.4' }}>
              {entity.attributes.description}
            </div>
          )}
          {entity.attributes?.instructions && (
            <div
              className="hitl-task-instructions text-secondary small mt-1 mb-2"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entity.attributes.instructions) }}
            />
          )}

          {/* Complex Provider / Clinical Entity Attribute Grid */}
          {entity.attributes && Object.keys(entity.attributes).length > 0 && (
            <div className="hitl-attributes-grid">
              {entity.attributes.npi && (
                <div className="attr-item">
                  <span className="attr-label">NPI:</span>
                  <span className="attr-val">{entity.attributes.npi}</span>
                </div>
              )}
              {entity.attributes.classification && (
                <div className="attr-item">
                  <span className="attr-label">Type:</span>
                  <span className="attr-val">{entity.attributes.classification}</span>
                </div>
              )}
              {entity.attributes.specialty && (
                <div className="attr-item">
                  <span className="attr-label">Specialty:</span>
                  <span className="attr-val">{entity.attributes.specialty}</span>
                </div>
              )}
              {entity.attributes.practiceName && (
                <div className="attr-item">
                  <span className="attr-label">Practice / Clinic:</span>
                  <span className="attr-val font-weight-bold text-primary">{entity.attributes.practiceName}</span>
                </div>
              )}
              {entity.attributes.phone && (
                <div className="attr-item">
                  <span className="attr-label">Phone:</span>
                  <span className="attr-val">{entity.attributes.phone}</span>
                </div>
              )}
              {(entity.attributes.modality || entity.attributes.testType || entity.attributes.type) && (
                <div className="attr-item">
                  <span className="attr-label">Type of Test:</span>
                  <span className="attr-val font-weight-bold text-primary">
                    {entity.attributes.modality || entity.attributes.testType || entity.attributes.type}
                  </span>
                </div>
              )}
              {entity.attributes.bodyPart && (
                <div className="attr-item">
                  <span className="attr-label">Body Part / Anatomy:</span>
                  <span className="attr-val">{entity.attributes.bodyPart}</span>
                </div>
              )}
              {entity.attributes.results &&
                entity.category !== 'test' &&
                entity.attributes.results !== entity.extractedValue && (
                  <div className="attr-item">
                    <span className="attr-label">Results / Impression:</span>
                    <span className="attr-val font-weight-bold text-dark">{entity.attributes.results}</span>
                  </div>
                )}
              {entity.attributes.date && (
                <div className="attr-item">
                  <span className="attr-label">Date:</span>
                  <span className="attr-val">{entity.attributes.date}</span>
                </div>
              )}
              {entity.attributes.facility && (
                <div className="attr-item">
                  <span className="attr-label">Facility:</span>
                  <span className="attr-val text-primary">{entity.attributes.facility}</span>
                </div>
              )}
              {entity.attributes.dosage && (
                <div className="attr-item">
                  <span className="attr-label">Dosage:</span>
                  <span className="attr-val font-weight-bold text-info">{entity.attributes.dosage}</span>
                </div>
              )}
              {entity.attributes.frequency && (
                <div className="attr-item">
                  <span className="attr-label">Frequency:</span>
                  <span className="attr-val">{entity.attributes.frequency}</span>
                </div>
              )}
              {entity.attributes.reaction && (
                <div className="attr-item">
                  <span className="attr-label">Reaction:</span>
                  <span className="attr-val font-weight-bold text-danger">{entity.attributes.reaction}</span>
                </div>
              )}
              {entity.attributes.workCapacity && (
                <div className="attr-item">
                  <span className="attr-label">Work Capacity:</span>
                  <span className="attr-val font-weight-bold text-warning">{entity.attributes.workCapacity}</span>
                </div>
              )}
              {entity.attributes.restrictions && (
                <div className="attr-item">
                  <span className="attr-label">Restrictions:</span>
                  <span className="attr-val">{entity.attributes.restrictions}</span>
                </div>
              )}
              {entity.attributes.impairmentRating && (
                <div className="attr-item">
                  <span className="attr-label">Impairment:</span>
                  <span className="attr-val font-weight-bold text-danger">{entity.attributes.impairmentRating}</span>
                </div>
              )}
              {entity.attributes.code && (
                <div className="attr-item">
                  <span className="attr-label">ICD-10:</span>
                  <span className="attr-val font-weight-bold text-success">{entity.attributes.code}</span>
                </div>
              )}
              {entity.attributes.anatomicalLevel && (
                <div className="attr-item">
                  <span className="attr-label">Anatomy:</span>
                  <span className="attr-val font-weight-bold text-info">{entity.attributes.anatomicalLevel}</span>
                </div>
              )}
              {entity.category === 'task' && (entity.attributes.dueDate || entity.dueDate) && (
                <div className="attr-item">
                  <span className="attr-label">Due Date:</span>
                  <span className="attr-val font-weight-bold text-danger">
                    <i className="far fa-calendar-alt mr-1" />
                    {entity.attributes.dueDate || entity.dueDate}
                    {entity.attributes.suggestedLeadDays ? (
                      <span
                        className="badge badge-light border text-muted ml-1 font-weight-normal"
                        style={{ fontSize: '0.7rem' }}
                      >
                        {entity.attributes.suggestedLeadDays}d lead
                      </span>
                    ) : null}
                  </span>
                </div>
              )}
              {entity.category === 'task' && (entity.attributes.providerName || entity.providerName) && (
                <div className="attr-item">
                  <span className="attr-label">Assigned Provider:</span>
                  <span className="attr-val font-weight-bold text-primary">
                    <i className="fas fa-user-md mr-1" />
                    {entity.attributes.providerName || entity.providerName}
                  </span>
                </div>
              )}
              {entity.category === 'task' && entity.attributes.taskCategory && (
                <div className="attr-item">
                  <span className="attr-label">Action Category:</span>
                  <span className="attr-val badge badge-light border text-secondary font-weight-normal">
                    {entity.attributes.taskCategory}
                  </span>
                </div>
              )}
              {entity.category === 'contact' && entity.attributes.role && (
                <div className="attr-item">
                  <span className="attr-label">Role:</span>
                  <span className="attr-val font-weight-bold text-info">{entity.attributes.role}</span>
                </div>
              )}
              {entity.category === 'contact' &&
                (entity.attributes.organizationOrProviderName || entity.attributes.practiceName) && (
                  <div className="attr-item">
                    <span className="attr-label">Facility / Provider:</span>
                    <span className="attr-val font-weight-bold text-primary">
                      {entity.attributes.organizationOrProviderName || entity.attributes.practiceName}
                    </span>
                  </div>
                )}
              {entity.category === 'contact' && entity.attributes.phone && (
                <div className="attr-item">
                  <span className="attr-label">Phone:</span>
                  <span className="attr-val">
                    {entity.attributes.phone}
                    {entity.attributes.extension ? ` ext. ${entity.attributes.extension}` : ''}
                  </span>
                </div>
              )}
              {entity.category === 'contact' && entity.attributes.email && (
                <div className="attr-item">
                  <span className="attr-label">Email:</span>
                  <span className="attr-val">{entity.attributes.email}</span>
                </div>
              )}
              {entity.category === 'contact' && entity.attributes.directAddress && (
                <div className="attr-item">
                  <span className="attr-label">Direct Address:</span>
                  <span className="attr-val text-primary" style={{ fontSize: '0.75rem' }}>
                    {entity.attributes.directAddress}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Medication Flags: Related to Injury, Pre-injury, Discontinued */}
          {entity.category === 'medication' && (
            <div className="hitl-med-flags mt-2 pt-2 border-top" onClick={e => e.stopPropagation()}>
              {!readOnly && entity.status !== 'rejected' ? (
                <div className="d-flex flex-wrap align-items-center" style={{ gap: '0.85rem', fontSize: '0.78rem' }}>
                  <label className="mb-0 d-flex align-items-center cursor-pointer font-weight-normal text-secondary">
                    <input
                      type="checkbox"
                      className="mr-1"
                      checked={Boolean(localMedFlags.relatedToInjury)}
                      onChange={e => handleMedFlagChange('relatedToInjury', e.target.checked)}
                    />
                    Related to Injury
                  </label>
                  <label className="mb-0 d-flex align-items-center cursor-pointer font-weight-normal text-secondary">
                    <input
                      type="checkbox"
                      className="mr-1"
                      checked={Boolean(localMedFlags.preInjury)}
                      onChange={e => handleMedFlagChange('preInjury', e.target.checked)}
                    />
                    Pre-injury
                  </label>
                  <label className="mb-0 d-flex align-items-center cursor-pointer font-weight-normal text-secondary">
                    <input
                      type="checkbox"
                      className="mr-1"
                      checked={Boolean(localMedFlags.discontinued)}
                      onChange={e => handleMedFlagChange('discontinued', e.target.checked)}
                    />
                    Discontinued
                  </label>
                </div>
              ) : (
                <div className="d-flex flex-wrap align-items-center" style={{ gap: '4px' }}>
                  {Boolean(localMedFlags.relatedToInjury) && (
                    <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>
                      Related to Injury
                    </span>
                  )}
                  {Boolean(localMedFlags.preInjury) && (
                    <span className="badge badge-warning text-dark" style={{ fontSize: '0.7rem' }}>
                      Pre-injury
                    </span>
                  )}
                  {Boolean(localMedFlags.discontinued) && (
                    <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>
                      Discontinued
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Associate Provider with Organization */}
          {entity.category === 'provider' && !readOnly && entity.status !== 'rejected' && (
            <div className="hitl-associate-org mt-2 pt-2 border-top" onClick={e => e.stopPropagation()}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="small font-weight-bold text-muted" style={{ fontSize: '0.78rem' }}>
                  <i className="fas fa-hospital mr-1" /> Associate with Practice / Facility:
                </span>
                {entity.attributes?.practiceName && (
                  <span className="badge badge-light border text-primary" style={{ fontSize: '0.72rem' }}>
                    Linked
                  </span>
                )}
              </div>
              <select
                className="form-control form-control-sm"
                value={isCustomPractice ? '__custom__' : entity.attributes?.practiceName || ''}
                onChange={e => {
                  if (e.target.value === '__custom__') {
                    setIsCustomPractice(true)
                  } else {
                    setIsCustomPractice(false)
                    if (onAssociateOrganization) onAssociateOrganization(entity.id, e.target.value)
                  }
                }}
                style={{ fontSize: '0.8rem', border: '1px solid #ced4da' }}
              >
                <option value="">-- No specific organization linked --</option>
                {availableOrganizations.map(org => (
                  <option key={org} value={org}>
                    {org}
                  </option>
                ))}
                <option value="__custom__">+ Enter custom practice / facility name...</option>
              </select>

              {isCustomPractice && (
                <div className="input-group input-group-sm mt-1">
                  <input
                    ref={customPracticeInputRef}
                    type="text"
                    className="form-control"
                    placeholder="Enter practice / clinic name"
                    value={customPracticeName}
                    onChange={e => setCustomPracticeName(e.target.value)}
                    style={{ fontSize: '0.8rem' }}
                  />
                  <div className="input-group-append">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        if (customPracticeName.trim()) {
                          if (onAssociateOrganization) onAssociateOrganization(entity.id, customPracticeName.trim())
                          setIsCustomPractice(false)
                        }
                      }}
                      style={{ fontSize: '0.75rem' }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setIsCustomPractice(false)}
                      style={{ fontSize: '0.75rem' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Provider Match Guidance Note */}
          {entity.category === 'provider' &&
            (() => {
              const pMatch =
                entity.providerMatch ||
                (entity.duplicateMatch
                  ? { type: 'case', matchedName: entity.duplicateMatch.existingName }
                  : { type: 'new' })
              if (pMatch.type === 'case') {
                return (
                  <div
                    className="py-1 px-2 mt-2 mb-1 small rounded d-flex align-items-center"
                    style={{
                      fontSize: '0.78rem',
                      backgroundColor: '#e6f4ea',
                      border: '1px solid #ceead6',
                      color: '#137333',
                    }}
                  >
                    <i className="fas fa-check-circle mr-2 flex-shrink-0" />
                    <span>
                      <strong>Matches Case Provider:</strong> &ldquo;{pMatch.matchedName || entity.extractedValue}
                      &rdquo; is already attached to this case. Accepting will confirm and merge newly extracted details
                      into this case.
                    </span>
                  </div>
                )
              }
              if (pMatch.type === 'library') {
                return (
                  <div
                    className="py-1 px-2 mt-2 mb-1 small rounded d-flex align-items-center"
                    style={{
                      fontSize: '0.78rem',
                      backgroundColor: '#eef2ff',
                      border: '1px solid #c7d2fe',
                      color: '#3730a3',
                    }}
                  >
                    <i className="fas fa-book-medical mr-2 flex-shrink-0" />
                    <span>
                      <strong>Matches Library Provider:</strong> Found &ldquo;
                      {pMatch.matchedName || entity.extractedValue}&rdquo; in your Provider Library. Accepting will link
                      this existing library provider to this case without creating duplicate records.
                    </span>
                  </div>
                )
              }
              return (
                <div
                  className="py-1 px-2 mt-2 mb-1 small rounded d-flex align-items-center"
                  style={{
                    fontSize: '0.78rem',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    color: '#495057',
                  }}
                >
                  <i className="fas fa-plus-circle mr-2 flex-shrink-0 text-secondary" />
                  <span>
                    <strong>New Provider:</strong> Not currently in your Library or on this Case. Accepting will create
                    a new entry in your Provider Library and link it to this case.
                  </span>
                </div>
              )
            })()}

          {/* Predecessor Accepted Banner with Merge Opportunity */}
          {entity.hasAcceptedPredecessor && entity.status !== 'merged' && entity.status !== 'rejected' && (
            <div
              className="alert alert-info py-1 px-2 mt-2 mb-1 small border-info d-flex justify-content-between align-items-center"
              style={{ fontSize: '0.8rem', backgroundColor: '#e8f4fd' }}
              onClick={e => e.stopPropagation()}
            >
              <span>
                <i className="fas fa-link mr-1 text-primary" />
                Occurrence #{entity.acceptedPredecessorIndex || 1} was Accepted. Merge details?
              </span>
              <button
                type="button"
                className="btn btn-xs btn-info text-white font-weight-bold ml-2 text-nowrap"
                onClick={() => onMerge && onMerge(entity.id, entity.acceptedPredecessorId)}
                style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem' }}
                title="Merge attributes from this occurrence into the accepted record"
              >
                <i className="fas fa-bolt mr-1" /> Merge
              </button>
            </div>
          )}

          {/* Negative Finding Warning Alert */}
          {entity.isNegativeFinding && (
            <div
              className="alert alert-warning py-1 px-2 mt-2 mb-1 small d-flex align-items-center"
              style={{ backgroundColor: '#fff3cd', borderColor: '#ffeeba' }}
              onClick={e => e.stopPropagation()}
            >
              <i className="fas fa-exclamation-triangle mr-2 text-warning flex-shrink-0" />
              <span>
                <strong>Negative Finding:</strong> Diagnostic test was negative for acute pathology. Recommend
                discarding.
              </span>
            </div>
          )}

          {/* Consolidated Candidate Breakdown Drawer */}
          {entity.isConsolidated && entity.occurrences && entity.occurrences.length > 1 && (
            <div
              className="hitl-consolidated-banner alert alert-primary py-1 px-2 mt-2 mb-1 small"
              style={{ backgroundColor: '#e8f4fd', borderColor: '#b8daff' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>
                  <i className="fas fa-layer-group mr-1 text-primary" />
                  <strong>Consolidated Candidate:</strong> {entity.clusterCount || entity.totalOccurrences} mentions
                  across documents.
                </span>
                <button
                  type="button"
                  className="btn btn-link btn-xs p-0 text-primary font-weight-bold"
                  onClick={() => setShowOccurrences(prev => !prev)}
                >
                  {showOccurrences ? 'Hide Mentions' : `View All (${entity.occurrences.length})`}
                </button>
              </div>
              {showOccurrences && (
                <div className="hitl-occurrences-list mt-2 pt-2 border-top" style={{ borderColor: '#bee5eb' }}>
                  {entity.occurrences.map((occ, oIdx) => (
                    <div
                      key={occ.id || oIdx}
                      className="small text-muted mb-1 p-1 rounded d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.7)' }}
                      role="button"
                      tabIndex={0}
                      title="Click to view this occurrence in document"
                      onClick={e => {
                        e.stopPropagation()
                        if (onSelect) onSelect(occ)
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.stopPropagation()
                          if (onSelect) onSelect(occ)
                        }
                      }}
                    >
                      <span className="text-truncate mr-2">
                        <i className="fas fa-file-alt text-primary mr-1" />
                        <strong>{occ.documentName || 'Document'}</strong>
                        {occ.page ? ` (p. ${occ.page})` : ''}: <em>{occ.extractedValue}</em>
                      </span>
                      <span
                        className="badge badge-light border text-primary flex-shrink-0"
                        style={{ fontSize: '0.68rem' }}
                      >
                        View in PDF
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Source Text Snippet with click-to-jump */}
          {entity.sourceSnippet && (
            <div
              className="hitl-snippet-box mt-2"
              title="Click to view in source document"
              onClick={e => {
                e.stopPropagation()
                if (onSelect) onSelect(entity)
              }}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.stopPropagation()
                  if (onSelect) onSelect(entity)
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <span className="font-weight-bold text-secondary">
                <i className="fas fa-quote-left mr-1 text-muted" /> Source Quote:
              </span>{' '}
              <span className="font-italic text-dark">
                &ldquo;
                {entity.sourceSnippet.length > 280
                  ? `${entity.sourceSnippet.slice(0, 260).trim()}...`
                  : entity.sourceSnippet}
                &rdquo;
              </span>
            </div>
          )}
        </>
      )}

      {/* Reject Reasons Selection Menu */}
      {showRejectMenu && !readOnly && (
        <div className="hitl-reject-menu my-2 p-2 bg-light border rounded" onClick={e => e.stopPropagation()}>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="small font-weight-bold text-danger" style={{ fontSize: '0.78rem' }}>
              <i className="fas fa-question-circle mr-1" /> Reason for skipping this element:
            </span>
            <button
              type="button"
              className="btn btn-link btn-xs text-muted p-0"
              onClick={() => {
                setShowRejectMenu(false)
                setShowCustomInput(false)
              }}
            >
              <i className="fas fa-times" />
            </button>
          </div>

          <div className="d-flex flex-wrap mb-2" style={{ gap: '4px' }}>
            {getRejectionOptions(entity.category).map(reason => (
              <button
                key={reason}
                type="button"
                className="btn btn-xs btn-outline-secondary text-dark font-weight-normal"
                onClick={() => handleSelectRejectReason(reason)}
                style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', backgroundColor: '#fff' }}
              >
                {reason}
              </button>
            ))}
          </div>

          {showCustomInput && (
            <form onSubmit={handleCustomReasonSubmit} className="d-flex" style={{ gap: '4px' }}>
              <input
                ref={customReasonInputRef}
                type="text"
                className="form-control form-control-sm flex-grow-1"
                value={customReason}
                onChange={e => setCustomReason(e.target.value)}
                placeholder="Enter specific clinical reason..."
                style={{ fontSize: '0.78rem' }}
              />
              <button type="submit" className="btn btn-xs btn-danger font-weight-bold" style={{ fontSize: '0.75rem' }}>
                Skip
              </button>
            </form>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {!readOnly && (
        <div
          className="hitl-card-actions mt-2 d-flex justify-content-between align-items-center"
          onClick={e => e.stopPropagation()}
        >
          {entity.status === 'accepted' || entity.status === 'rejected' || entity.status === 'merged' ? (
            <div className="d-flex align-items-center" style={{ gap: '6px' }}>
              {entity.status === 'accepted' && (
                <button
                  type="button"
                  className="btn btn-xs btn-outline-primary"
                  onClick={() => setIsEditing(true)}
                  style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                >
                  <i className="fas fa-pencil-alt mr-1" /> Re-edit
                </button>
              )}
              <button
                type="button"
                className="btn btn-xs btn-outline-secondary"
                onClick={() => onUndo(entity.id)}
                style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
              >
                <i className="fas fa-undo mr-1" /> Undo
              </button>
            </div>
          ) : (
            <>
              <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                <button
                  type="button"
                  className="btn btn-xs btn-outline-primary"
                  onClick={() => setIsEditing(true)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  <i className="fas fa-pencil-alt mr-1" />{' '}
                  {entity.status === 'edited' ? 'Re-edit' : entity.category === 'test' ? 'Edit Results' : 'Edit'}
                </button>
                {entity.status === 'edited' && (
                  <button
                    type="button"
                    className="btn btn-xs btn-outline-secondary"
                    onClick={() => onUndo(entity.id)}
                    title="Reset to original extraction"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    <i className="fas fa-undo mr-1" /> Reset
                  </button>
                )}
              </div>
              <div className="d-flex flex-column align-items-end" style={{ gap: '2px' }}>
                <div className="d-flex" style={{ gap: '6px' }}>
                  <button
                    type="button"
                    className="btn btn-xs btn-outline-danger"
                    onClick={() => setShowRejectMenu(prev => !prev)}
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
                  >
                    <i className="fas fa-times mr-1" /> Skip
                  </button>
                  {entity.isNegativeFinding ? (
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-danger font-weight-bold"
                      onClick={() => onAccept(entity.id)}
                      title="Discard Negative Finding"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem' }}
                    >
                      <i className="fas fa-trash-alt mr-1" /> Discard
                    </button>
                  ) : entity.isConsolidated ? (
                    <button
                      type="button"
                      className="btn btn-xs btn-success text-white font-weight-bold"
                      onClick={handleAcceptClick}
                      title={acceptActionLabel}
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem' }}
                    >
                      <i className="fas fa-layer-group mr-1" /> Consolidate
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-xs btn-success text-white font-weight-bold"
                      onClick={handleAcceptClick}
                      title={acceptActionLabel}
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem' }}
                    >
                      <i className="fas fa-check mr-1" /> {entity.status === 'edited' ? 'Accept Edit' : 'Accept'}
                    </button>
                  )}
                </div>
                <span
                  className="text-primary font-weight-bold hitl-action-hint text-truncate"
                  title={acceptActionLabel}
                  style={{ fontSize: '0.68rem', letterSpacing: '0.2px', maxWidth: '170px' }}
                >
                  <i className="fas fa-arrow-right mr-1" style={{ fontSize: '0.6rem' }} />
                  {acceptActionLabel}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

EntityCard.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    category: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired,
    currentValue: PropTypes.string,
    extractedValue: PropTypes.string,
    attributes: PropTypes.object,
    sourceSnippet: PropTypes.string,
    documentId: PropTypes.string,
    documentName: PropTypes.string,
    page: PropTypes.number,
    lineNumber: PropTypes.number,
    status: PropTypes.string,
    rejectionReason: PropTypes.string,
    totalOccurrences: PropTypes.number,
    occurrenceIndex: PropTypes.number,
    hasAcceptedPredecessor: PropTypes.bool,
    acceptedPredecessorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    acceptedPredecessorIndex: PropTypes.number,
    mergedIntoTitle: PropTypes.string,
    isNpiVerified: PropTypes.bool,
    isNegativeFinding: PropTypes.bool,
    isConsolidated: PropTypes.bool,
    isPreExisting: PropTypes.bool,
    isRadiologyImpression: PropTypes.bool,
    clusterCount: PropTypes.number,
    occurrences: PropTypes.arrayOf(PropTypes.object),
    duplicateMatch: PropTypes.shape({
      existingName: PropTypes.string,
      confidence: PropTypes.number,
      existingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    providerMatch: PropTypes.shape({
      type: PropTypes.string,
      matchedName: PropTypes.string,
      matchedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      reason: PropTypes.string,
      confidence: PropTypes.number,
    }),
  }).isRequired,
  isActive: PropTypes.bool,
  onSelect: PropTypes.func,
  onAccept: PropTypes.func,
  onReject: PropTypes.func,
  onMerge: PropTypes.func,
  onAssociateOrganization: PropTypes.func,
  onEdit: PropTypes.func,
  onUndo: PropTypes.func,
  onUpdateMedicationFlags: PropTypes.func,
  availableOrganizations: PropTypes.arrayOf(PropTypes.string),
  readOnly: PropTypes.bool,
}
